import { supabase } from '@/integrations/supabase/client';
;
import { TableSchemas } from '../../tableSchemas';

/**
 * A utility to throttle Supabase API calls and prevent excessive requests
 */
const lastRequestTime: { [key: string]: number } = {};
const MIN_REQUEST_INTERVAL = 500; // Reduced to 500ms (was 2000ms)
const pendingRequests: { [key: string]: Promise<unknown> } = {};
const MAX_CONCURRENT_REQUESTS = 5; // Limit concurrent requests
let activeRequests = 0;

/**
 * Throttled Supabase API call wrapper to prevent excessive requests
 * @param requestKey A unique key to identify this type of request
 * @param requestFn The actual Supabase request function to execute
 * @returns The result of the Supabase request
 */
export async function throttledSupabaseRequest<T>(
	requestKey: string,
	requestFn: () => Promise<T>
): Promise<T> {
	const now = Date.now();
	const lastTime = lastRequestTime[requestKey] || 0;
	const timeSinceLastRequest = now - lastTime;

	// If we already have a pending request for this key, return that instead of making a new one
	if (pendingRequests[requestKey]) {
		return pendingRequests[requestKey] as Promise<T>;
	}

	// Wait if we have too many active requests
	while (activeRequests >= MAX_CONCURRENT_REQUESTS) {
		await new Promise(resolve => setTimeout(resolve, 100));
	}

	// If we're making requests too quickly, delay this one but don't skip it
	if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
		const delay = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
		await new Promise(resolve => setTimeout(resolve, delay));
	}

	// Update the last request time
	lastRequestTime[requestKey] = Date.now();
	activeRequests++;

	// Execute the request and store it in pendingRequests
	try {
		pendingRequests[requestKey] = requestFn();
		const result = await pendingRequests[requestKey] as T;
		return result;
	} finally {
		// Clean up the pending request
		delete pendingRequests[requestKey];
		activeRequests--;
	}
}

/**
 * A throttled version of supabase.from().select()
 */
export async function throttledSelect<T>(
	table: string,
	query: string,
	options?: Record<string, unknown>
): Promise<{ data: T[] | null; error: unknown }> {
	const requestKey = `select:${table}:${query}:${JSON.stringify(options)}`;
	return throttledSupabaseRequest(requestKey, async () => {
		const schemaTable = table;
		return await supabase.from(schemaTable).select(query, options);
	});
}

/**
 * Safely handle Supabase authentication errors
 */
export async function safeAuthRequest<T>(
	requestFn: () => Promise<T>,
	errorMessage: string = 'Authentication error'
): Promise<T> {
  try {
		return await requestFn();
	} catch (error) {
		// Define a type guard for error objects
		const isAuthError = (err: unknown): err is {
			status?: number;
			code?: string;
			message?: string
		} => {
			return typeof err === 'object' && err !== null;
		};
		// Check if this is an authentication error
		if (
			isAuthError(error) && (
				error.status === 401 ||
				error.code === 'PGRST301' ||
				(typeof error.message === 'string' &&
					(error.message.includes('auth') ||
						error.message.includes('JWT') ||
						error.message.includes('token') ||
						error.message.includes('session')))
			)
		) {
			// Force refresh the session
			try {
				await supabase.auth.refreshSession();
			} catch {
				// ignore refresh error
			}
		}
		throw new Error(errorMessage);
	}
}

/**
 * Gets the correct column name for a table to prevent errors
 * @param tableName The table name in the database
 * @param columnAlias The column name or alias you're trying to use
 * @returns The correct column name if it exists, or the original if not found
 */
export function getValidColumnName(tableName: keyof typeof TableSchemas, columnAlias: string): string {
	// If the table doesn't exist in our schema, return the original
	if (!TableSchemas[tableName]) {
		return columnAlias;
	}

	// Direct match to an actual column name
	if (columnAlias in TableSchemas[tableName]) {
		return columnAlias;
	}

	// Handle common cases for employee names
	if (tableName === 'employees' && columnAlias === 'name') {
		// For sorting/ordering, we'll default to first_name
		return 'first_name';
	}

	// Log a warning for debugging
	return columnAlias;
}



/**
 * Format an employee name from first and last name
 */
/**
 * Formats an employee's name from firstName and lastName fields or first_name/last_name
 * @param employee The employee object from the database or UI
 * @returns A formatted full name
 */
export function formatEmployeeName(employee: unknown): string {
	if (typeof employee === 'object' && employee !== null) {
		const e = employee as {
			name?: string;
			firstName?: string;
			lastName?: string;
			first_name?: string;
			last_name?: string;
		};
		if (e.name) return e.name;
		if (e.firstName && e.lastName) return `${e.firstName} ${e.lastName}`;
		if (e.firstName) return e.firstName;
		if (e.lastName) return e.lastName;
		if (e.first_name && e.last_name) return `${e.first_name} ${e.last_name}`;
		if (e.first_name) return e.first_name;
		if (e.last_name) return e.last_name;
	}
	return '';
}
